import { StackGrid } from "@data-display/layout/StackGrid";
import { Box, Button, Typography } from "@mui/material";
import clsx from "clsx";
import { add, format, getDay, getDaysInMonth, nextMonday } from "date-fns";
import { fr } from "date-fns/locale";
import { type ReactNode, useMemo } from "react";
import { useDRPContext } from "../../context";
import { useDRDPContext } from "./context";
import { DRDPDayItemContainer, dayItemClasses } from "./styles";
import type { IDRDPDay, IDRDPDayItem } from "./types";

const days: ReactNode[] = [];

const monday = nextMonday(new Date());

for (let i = 0; i < 7; i++) {
  days.push(
    <Typography
      key={i}
      sx={{
        width: "36px",
      }}
    >
      {format(add(monday, { days: i }), "E", { locale: fr }).slice(0, -1)}
    </Typography>
  );
}

const DRDPDayItem = ({ date }: IDRDPDayItem) => {
  const { minDate, maxDate, endDateDisabled, timeZone } = useDRPContext();
  const { handleMouseEnter, handleMouseLeave, handleSelect, isSelected, isBetween, selectRange } = useDRDPContext();

  const selected = isSelected(date);
  const between = selected === "false" && isBetween(date);
  const disabled = (minDate && date < minDate) || (maxDate && date > maxDate);

  return (
    <DRDPDayItemContainer
      className={clsx(dayItemClasses.root, {
        [dayItemClasses.divStartSelected]: selected === "start" && selectRange.endDate,
        [dayItemClasses.divEndSelected]: selected === "end" && selectRange.startDate,
        [dayItemClasses.between]: between && selected === "false",
      })}
    >
      <Button
        className={clsx(dayItemClasses.button, {
          [dayItemClasses.selected]: selected !== "false",
        })}
        onMouseEnter={() => handleMouseEnter(date)}
        onMouseLeave={handleMouseLeave}
        onClick={() => handleSelect(date, endDateDisabled)}
        disabled={disabled}
      >
        {date.toLocaleDateString("fr-FR", { day: "numeric", timeZone })}
      </Button>
    </DRDPDayItemContainer>
  );
};

export const DRDPDay = ({ index }: IDRDPDay) => {
  const { monthYearRange } = useDRDPContext();

  const items = useMemo(() => {
    const date = monthYearRange[index];
    const day = getDay(date);
    const offset = day === 0 ? 6 : day - 1;
    const numDays = getDaysInMonth(date);
    const items: ReactNode[] = [];
    for (let i = 0; i < numDays + offset; i++) {
      if (i < offset) {
        items.push(
          <Box
            key={i}
            sx={{
              height: "32px",
            }}
          />
        );
      } else {
        items.push(<DRDPDayItem key={i} date={new Date(date.getFullYear(), date.getMonth(), i + 1 - offset)} />);
      }
    }
    return items;
  }, [index, monthYearRange]);

  return (
    <>
      <StackGrid textAlign="center" perLine={7}>
        {days}
      </StackGrid>
      <StackGrid textAlign="center" perLine={7} rowGap={0.5} boxSizing="border-box">
        {items}
      </StackGrid>
    </>
  );
};
